import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/** A page's side margins: 18 px on a phone, 27 px from 640 px up. */
export const PAGE_GUTTER = "px-4 sm:px-6";

/** A strip that runs to the screen's edges inside a page, its content still in line with the page's. */
export const PAGE_BLEED = "-mx-4 px-4 sm:-mx-6 sm:px-6";

interface PageProps {
  /** The page's title, its `<h1>`; the home page leaves it out and greets instead. */
  title?: string;
  /** One line under the title. */
  description?: ReactNode;
  /** Controls right of the title, such as a period switch. */
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * A signed-in page inside the app shell: its width, its title and the content.
 * On a phone its side margins are 18 px instead of 27, so tables and cards get
 * the room. `PAGE_BLEED` lets a full-width strip, such as the scrolling tabs of
 * `SectionLayout` in Settings, run to the screen's edges while its content
 * stays in line with the page.
 */
export function Page({ title, description, actions, children }: PageProps) {
  return (
    <div className={cn("mx-auto w-full max-w-5xl py-8", PAGE_GUTTER)}>
      {(title || actions) && (
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            {title && <h1 className="font-heading text-primary text-2xl font-bold">{title}</h1>}
            {description && <p className="text-muted mt-1 text-sm">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </div>
  );
}
